import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientMutation, clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager";
import { saveAs } from "file-saver";
import {
  EXPORT_MAX_PAGES, EXPORT_PAGE_SIZE, LOG_MAX_PAGES, LOG_PAGE_SIZE, LOG_POLL_MAX_PAGES, STATS_DAYS, UNITS_MAX_PAGES, UNITS_PAGE_SIZE,
  buildTransmissionsCsv, mergeTransmissions, parseProviderData
} from "../transmissions";

// The account has at most one integration per catalog app, and the catalog is short: one page (API max 200) holds them all
const SEARCH_LIMIT = 200;

const INIT_STATE = {
  loading: false,
  integrations: [],
  error: null,
  // Catalog apps the account can connect (public integrations, from the API)
  catalog: [],
  catalogLoading: false,
  catalogError: null,
  openWizard: false,
  openDelete: false,
  integrationsToDelete: [],
  deleting: false,
  deleteResponse: null,
  // Edit side panel of one integration (design "Edit integration"): its id while open, and the save in progress
  edit: { open: false, integrationId: null, saving: false },
  // Integrations whose vehicle sync was started from this screen; the push notification ends it
  syncingIds: [],
  // Their progress by id: vehicles read so far (RUNNING pushes), plus the lastSync the integration had (`since`) and
  // when the sync started, for the fallback that polls the result
  syncProgress: {},
  flashId: null,
  // Linked vehicles console of one integration (design "CoastPay linked vehicles")
  console: { open: false, integrationId: null },
  // Its provider vehicles (null until loaded) and the daily transmission summaries of the last 7 account days
  units: null,
  unitsLoading: false,
  unitsError: null,
  stats: null,
  statsLoading: false,
  statsError: null,
  // Transmission log panel: its filters and the latest rows of the range, newest first
  log: { open: false, unitId: null, errorsOnly: false, range: "today", rows: [], hasMore: false, loading: false, error: null, requestId: null, exporting: false }
};

const route = "account/views/apps/redux/slice";

const parseWizardConfig = (value) => {
  if (value && typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};

const INTEGRATION_FIELDS = `
  id
  integrationId
  integrationCode
  integrationName
  integrationType
  status
  apiKeyMasked
  icon
  color
  syncsVehicles
  apiKeyMaxLength
  linkedVehicles
  lastSync {
    finishedOn
    status
    source
    providerVehicles
    matched
    errorMessage
  }
  telemetry {
    lastOn
    lastStatus
    lastEvent
    lastUnitId
    sentToday
    errorsToday
    timeZone
  }
  createdOn
  updatedOn
`;

export const accountAppsSearch = createAsyncThunk("account/apps/accountAppsSearch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await clientQuery(
        `
          query searchIntegrations ($openSearch: String, $limit: Int, $offset: Int) {
            searchIntegrations (openSearch: $openSearch, limit: $limit, offset: $offset) {
              items { ${INTEGRATION_FIELDS} }
              total
            }
          }
        `,
        { limit: SEARCH_LIMIT, offset: 0 },
        endpoints.GRAPHQL_GENERAL
      );
      return response?.searchIntegrations?.items || [];
    } catch (exc) {
      ExceptionManager(exc, route, "accountAppsSearch");
      return rejectWithValue(exc);
    }
  }
);

export const accountAppsSearchCatalog = createAsyncThunk("account/apps/accountAppsSearchCatalog",
  async (_, { rejectWithValue }) => {
    try {
      const response = await clientQuery(
        `
          query searchIntegrationsCatalog ($openSearch: String, $limit: Int, $offset: Int) {
            searchIntegrationsCatalog (openSearch: $openSearch, limit: $limit, offset: $offset) {
              items {
                id
                code
                name
                integrationType
                apiKeyMaxLength
                syncsVehicles
                wizardConfig
              }
              total
            }
          }
        `,
        { limit: SEARCH_LIMIT, offset: 0 },
        endpoints.GRAPHQL_GENERAL
      );
      // wizardConfig is AWSJSON (a JSON string), already validated by the API
      return (response?.searchIntegrationsCatalog?.items || [])
        .map((item) => ({ ...item, wizardConfig: parseWizardConfig(item.wizardConfig) }))
        .filter((item) => item.wizardConfig?.steps?.length);
    } catch (exc) {
      ExceptionManager(exc, route, "accountAppsSearchCatalog");
      return rejectWithValue({ error: exc?.error });
    }
  }
);

// The key check the wizard shows: whether the app accepted the key, the account it belongs to and the vehicles
// enabled for integration (CoastPay: GET /v2/account + /v2/vehicles); rejects when the app can't be reached
export const accountAppsCheckApiKey = createAsyncThunk("account/apps/accountAppsCheckApiKey",
  async ({ integrationCode, apiKey }, { rejectWithValue }) => {
    try {
      const response = await clientQuery(
        `
          query checkCompanyIntegrationApiKey ($integrationCode: enumIntegrationCode!, $apiKey: String!) {
            checkCompanyIntegrationApiKey (integrationCode: $integrationCode, apiKey: $apiKey) {
              authenticated
              accountId
              accountName
              accountStatus
              vehicleAccess
              hasVehicles
            }
          }
        `,
        { integrationCode, apiKey },
        endpoints.GRAPHQL_GENERAL
      );
      return response?.checkCompanyIntegrationApiKey || { authenticated: false };
    } catch (exc) {
      ExceptionManager(exc, route, "accountAppsCheckApiKey");
      return rejectWithValue({ error: exc?.error });
    }
  }
);

// Creates the integration and, for apps that sync vehicles, starts the sync right away
export const accountAppsCreate = createAsyncThunk("account/apps/accountAppsCreate",
  async ({ integrationCode, apiKey, syncsVehicles }, { rejectWithValue }) => {
    let integration;
    try {
      const response = await clientMutation(
        `
          mutation createCompanyIntegration ($integrationCode: enumIntegrationCode!, $apiKey: String!) {
            createCompanyIntegration (integrationCode: $integrationCode, apiKey: $apiKey) { ${INTEGRATION_FIELDS} }
          }
        `,
        { integrationCode, apiKey },
        endpoints.GRAPHQL_GENERAL
      );
      integration = response?.createCompanyIntegration;
    } catch (exc) {
      ExceptionManager(exc, route, "accountAppsCreate");
      return rejectWithValue({ error: exc?.error });
    }
    if (!integration?.id) return rejectWithValue({ error: null });

    let syncStarted = false;
    if (syncsVehicles) {
      try {
        const response = await clientMutation(
          `
            mutation runCompanyIntegration ($id: Int!) {
              runCompanyIntegration (id: $id)
            }
          `,
          { id: integration.id },
          endpoints.GRAPHQL_GENERAL
        );
        syncStarted = response?.runCompanyIntegration === true;
      } catch (exc) {
        // The integration is saved; the daily sync picks it up even if this start failed
        ExceptionManager(exc, route, "accountAppsCreate.runCompanyIntegration");
      }
    }
    return { integration, syncStarted, startedAt: Date.now() };
  }
);

// Rotates the key and/or changes the status (Active / Inactive, the switch of the vehicle sync and the transmission);
// only what changed is sent
export const accountAppsUpdate = createAsyncThunk("account/apps/accountAppsUpdate",
  async ({ id, apiKey, status }, { rejectWithValue }) => {
    try {
      const variables = { id };
      if (apiKey) variables.apiKey = apiKey;
      if (status) variables.status = status;
      const response = await clientMutation(
        `
          mutation updateCompanyIntegration ($id: Int!, $apiKey: String, $status: enumCompanyIntegrationStatus) {
            updateCompanyIntegration (id: $id, apiKey: $apiKey, status: $status) { ${INTEGRATION_FIELDS} }
          }
        `,
        variables,
        endpoints.GRAPHQL_GENERAL
      );
      const integration = response?.updateCompanyIntegration;
      if (!integration?.id) return rejectWithValue({ error: null });
      return integration;
    } catch (exc) {
      ExceptionManager(exc, route, "accountAppsUpdate");
      return rejectWithValue({ error: exc?.error });
    }
  }
);

/**
 * Decrypted API key of an integration, for the copy button. Not a thunk on purpose: the key never goes through an
 * action or the store. px-cloud audits every reveal and redacts the key in its logs.
 */
export const revealIntegrationApiKey = async (id) => {
  try {
    const response = await clientMutation(
      `
        mutation revealCompanyIntegrationApiKey ($id: Int!) {
          revealCompanyIntegrationApiKey (id: $id) { id apiKey }
        }
      `,
      { id },
      endpoints.GRAPHQL_GENERAL
    );
    const apiKey = response?.revealCompanyIntegrationApiKey?.apiKey;
    if (!apiKey) throw new Error("The API key could not be read");
    return apiKey;
  } catch (exc) {
    ExceptionManager(exc, route, "revealIntegrationApiKey");
    throw exc;
  }
};

export const accountAppsDelete = createAsyncThunk("account/apps/accountAppsDelete",
  async (integrations, { rejectWithValue }) => {
    try {
      const results = await Promise.all(integrations.map(({ id }) => clientMutation(
        `
          mutation deleteCompanyIntegration ($id: Int!) {
            deleteCompanyIntegration (id: $id)
          }
        `,
        { id },
        endpoints.GRAPHQL_GENERAL
      )));
      return { ids: integrations.map(({ id }) => id), deleted: results.length };
    } catch (exc) {
      ExceptionManager(exc, route, "accountAppsDelete");
      return rejectWithValue({ error: exc?.error });
    }
  }
);

const UNIT_FIELDS = `
  id
  companyIntegrationId
  unitId
  unitLabel
  providerUnitId
  providerName
  vin
  licensePlate
  status
  providerData
  createdOn
  updatedOn
`;

const TRANSMISSION_FIELDS = `
  createdOn
  unitId
  providerUnitId
  event
  status
  httpStatus
  durationMs
  attempts
  unitTime
  latitude
  longitude
  speedMph
  odometer
  errorMessage
  endpoint
`;

// Every provider vehicle of the integration: the API pages by 200, the console filters and searches on the client
export const accountAppsSearchUnits = createAsyncThunk("account/apps/accountAppsSearchUnits",
  async (companyIntegrationId, { rejectWithValue }) => {
    try {
      const units = [];
      for (let page = 0; page < UNITS_MAX_PAGES; page++) {
        const response = await clientQuery(
          `
            query searchCompanyIntegrationUnits ($companyIntegrationId: Int!, $limit: Int, $offset: Int) {
              searchCompanyIntegrationUnits (companyIntegrationId: $companyIntegrationId, limit: $limit, offset: $offset) {
                items { ${UNIT_FIELDS} }
                total
              }
            }
          `,
          { companyIntegrationId, limit: UNITS_PAGE_SIZE, offset: page * UNITS_PAGE_SIZE },
          endpoints.GRAPHQL_GENERAL
        );
        const { items = [], total = 0 } = response?.searchCompanyIntegrationUnits || {};
        units.push(...items.map((item) => ({ ...item, providerData: parseProviderData(item.providerData) })));
        if (!items.length || units.length >= total) break;
      }
      return units;
    } catch (exc) {
      ExceptionManager(exc, route, "accountAppsSearchUnits");
      return rejectWithValue({ error: exc?.error });
    }
  }
);

// Daily transmission summaries of the integration vehicles for the last account days (oldest first)
export const accountAppsTransmissionStats = createAsyncThunk("account/apps/accountAppsTransmissionStats",
  async ({ companyIntegrationId, days = STATS_DAYS }, { rejectWithValue }) => {
    try {
      const response = await clientQuery(
        `
          query getCompanyIntegrationTransmissionStats ($companyIntegrationId: Int!, $days: Int) {
            getCompanyIntegrationTransmissionStats (companyIntegrationId: $companyIntegrationId, days: $days) {
              timeZone
              days
              items { unitId day sent errors lastOn lastStatus lastEvent lastHttpStatus lastErrorOn lastErrorMessage }
            }
          }
        `,
        { companyIntegrationId, days },
        endpoints.GRAPHQL_GENERAL
      );
      const stats = response?.getCompanyIntegrationTransmissionStats;
      return { timeZone: stats?.timeZone || null, days: stats?.days || [], items: stats?.items || [] };
    } catch (exc) {
      ExceptionManager(exc, route, "accountAppsTransmissionStats");
      return rejectWithValue({ error: exc?.error });
    }
  }
);

// Pages of transmissions (newest first) until `max` rows, `maxPages` pages or the end of the range
const fetchTransmissions = async ({ companyIntegrationId, unitId, from, to, errorsOnly }, limit, max, maxPages) => {
  const rows = [];
  let nextToken = null;
  for (let page = 0; page < maxPages; page++) {
    const response = await clientQuery(
      `
        query searchCompanyIntegrationTransmissions ($companyIntegrationId: Int!, $unitId: Int, $from: String!, $to: String!, $errorsOnly: Boolean, $limit: Int, $nextToken: String) {
          searchCompanyIntegrationTransmissions (companyIntegrationId: $companyIntegrationId, unitId: $unitId, from: $from, to: $to, errorsOnly: $errorsOnly, limit: $limit, nextToken: $nextToken) {
            items { ${TRANSMISSION_FIELDS} }
            nextToken
          }
        }
      `,
      { companyIntegrationId, unitId: unitId || null, from, to, errorsOnly: !!errorsOnly, limit, nextToken },
      endpoints.GRAPHQL_GENERAL
    );
    const result = response?.searchCompanyIntegrationTransmissions || {};
    rows.push(...(result.items || []));
    nextToken = result.nextToken || null;
    if (!nextToken || rows.length >= max) break;
  }
  return { rows: rows.slice(0, max), hasMore: !!nextToken || rows.length > max };
};

// The latest rows of the log range ("replace"), or the ones newer than the panel shows ("prepend", polling)
export const accountAppsSearchTransmissions = createAsyncThunk("account/apps/accountAppsSearchTransmissions",
  async ({ mode = "replace", ...search }, { rejectWithValue }) => {
    try {
      const { rows, hasMore } = await fetchTransmissions(search, LOG_PAGE_SIZE, LOG_PAGE_SIZE, mode === "prepend" ? LOG_POLL_MAX_PAGES : LOG_MAX_PAGES);
      return { mode, rows, hasMore, receivedAt: Date.now() };
    } catch (exc) {
      ExceptionManager(exc, route, "accountAppsSearchTransmissions");
      return rejectWithValue({ mode, error: exc?.error });
    }
  }
);

// Every transmission of the range as a CSV built here from the API pages (there is no export endpoint)
export const accountAppsExportTransmissions = createAsyncThunk("account/apps/accountAppsExportTransmissions",
  async ({ fileName, timeZone, unitsById, labels, ...search }, { rejectWithValue }) => {
    try {
      const { rows, hasMore } = await fetchTransmissions(search, EXPORT_PAGE_SIZE, EXPORT_PAGE_SIZE * EXPORT_MAX_PAGES, EXPORT_MAX_PAGES);
      if (rows.length) {
        // BOM: Excel opens it as UTF-8 (accents of the Spanish headers and events)
        saveAs(new Blob(["\uFEFF" + buildTransmissionsCsv(rows, { timeZone, unitsById, labels })], { type: "text/csv;charset=utf-8" }), fileName);
      }
      return { rows: rows.length, truncated: hasMore };
    } catch (exc) {
      ExceptionManager(exc, route, "accountAppsExportTransmissions");
      return rejectWithValue({ error: exc?.error });
    }
  }
);

// A log answer belongs to the filters the panel shows now
const matchesLogFilter = (log, arg) =>
  (arg.unitId || null) === (log.unitId || null) && !!arg.errorsOnly === log.errorsOnly && (arg.range || log.range) === log.range;

const accountAppsRedux = createSlice({
  name: "account/apps",
  initialState: INIT_STATE,
  reducers: {
    reset() {
      return INIT_STATE;
    },
    openAppsWizard(state, { payload }) {
      state.openWizard = !!payload;
    },
    openAppsDelete(state, { payload }) {
      state.openDelete = !!payload?.open;
      state.integrationsToDelete = payload?.open ? payload.integrations || [] : [];
      state.deleteResponse = null;
    },
    // Opens the edit panel of an integration (its id) or closes it (null)
    openAppsEdit(state, { payload }) {
      state.edit = { open: !!payload, integrationId: payload || null, saving: false };
    },
    clearAppsFlash(state) {
      state.flashId = null;
    },
    appsSyncFinished(state, { payload }) {
      state.syncingIds = state.syncingIds.filter((id) => id !== payload);
      delete state.syncProgress[payload];
    },
    // RUNNING push of a sync started here: vehicles processed so far
    appsSyncProgress(state, { payload: { id, processed } }) {
      if (!state.syncingIds.includes(id)) return;
      state.syncProgress[id] = { ...state.syncProgress[id], processed: Number(processed) || 0 };
    },
    // Opens the console of an integration (its id) or closes it (null), dropping its data
    openAppsConsole(state, { payload }) {
      const open = !!payload;
      state.console = { open, integrationId: open ? payload : null };
      state.units = null;
      state.unitsLoading = false;
      state.unitsError = null;
      state.stats = null;
      state.statsLoading = false;
      state.statsError = null;
      state.log = { ...INIT_STATE.log };
    },
    // Opens the log for the whole integration, one vehicle (unitId) or the errors only; closes it with open: false
    openAppsLog(state, { payload }) {
      const open = !!payload?.open;
      state.log = { ...INIT_STATE.log, open, unitId: open ? payload.unitId || null : null, errorsOnly: open ? !!payload.errorsOnly : false };
    },
    setAppsLogFilter(state, { payload }) {
      if (payload?.unitId !== undefined) state.log.unitId = payload.unitId || null;
      if (payload?.errorsOnly !== undefined) state.log.errorsOnly = !!payload.errorsOnly;
      if (payload?.range) state.log.range = payload.range;
    }
  },
  extraReducers: (builder) => {
    // A silent search (the grid following the transmission log) does not show the loading state
    builder.addCase(accountAppsSearch.pending, (state, { meta }) => {
      if (!meta.arg?.silent) state.loading = true;
      state.error = null;
    });
    builder.addCase(accountAppsSearch.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.integrations = payload;
    });
    builder.addCase(accountAppsSearch.rejected, (state, { payload, meta }) => {
      state.loading = false;
      // A failed background refresh keeps the rows on screen without an error toast; the next one retries
      if (!meta.arg?.silent) state.error = payload || true;
    });

    builder.addCase(accountAppsSearchCatalog.pending, (state) => {
      state.catalogLoading = true;
      state.catalogError = null;
    });
    builder.addCase(accountAppsSearchCatalog.fulfilled, (state, { payload }) => {
      state.catalogLoading = false;
      state.catalog = payload;
    });
    builder.addCase(accountAppsSearchCatalog.rejected, (state, { payload }) => {
      state.catalogLoading = false;
      state.catalogError = payload || true;
    });

    builder.addCase(accountAppsCreate.fulfilled, (state, { payload }) => {
      const { integration, syncStarted, startedAt } = payload;
      state.openWizard = false;
      state.integrations = [integration, ...state.integrations.filter((item) => item.id !== integration.id)];
      state.flashId = integration.id;
      if (syncStarted) {
        state.syncingIds = [...state.syncingIds, integration.id];
        state.syncProgress[integration.id] = { processed: 0, since: integration.lastSync?.finishedOn || null, startedAt: startedAt || null };
      }
    });

    builder.addCase(accountAppsUpdate.pending, (state) => {
      state.edit.saving = true;
    });
    // The answer replaces the row; its telemetry is kept when the answer brings none (the next silent reload refreshes it)
    builder.addCase(accountAppsUpdate.fulfilled, (state, { payload }) => {
      state.integrations = state.integrations.map((item) => (item.id === payload.id ? { ...item, ...payload, telemetry: payload.telemetry || item.telemetry } : item));
      state.edit = { ...INIT_STATE.edit };
    });
    builder.addCase(accountAppsUpdate.rejected, (state) => {
      state.edit.saving = false;
    });

    builder.addCase(accountAppsDelete.pending, (state) => {
      state.deleting = true;
      state.deleteResponse = null;
    });
    builder.addCase(accountAppsDelete.fulfilled, (state, { payload }) => {
      state.deleting = false;
      state.openDelete = false;
      state.integrationsToDelete = [];
      state.integrations = state.integrations.filter((item) => !payload.ids.includes(item.id));
      state.syncingIds = state.syncingIds.filter((id) => !payload.ids.includes(id));
      payload.ids.forEach((id) => { delete state.syncProgress[id]; });
      state.deleteResponse = payload;
      if (payload.ids.includes(state.console.integrationId)) {
        state.console = { ...INIT_STATE.console };
        state.log = { ...INIT_STATE.log };
      }
      if (payload.ids.includes(state.edit.integrationId)) state.edit = { ...INIT_STATE.edit };
    });
    builder.addCase(accountAppsDelete.rejected, (state, { payload }) => {
      state.deleting = false;
      state.deleteResponse = { error: payload?.error || true };
    });

    builder.addCase(accountAppsSearchUnits.pending, (state) => {
      state.unitsLoading = true;
      state.unitsError = null;
    });
    builder.addCase(accountAppsSearchUnits.fulfilled, (state, { payload }) => {
      state.unitsLoading = false;
      state.units = payload;
    });
    builder.addCase(accountAppsSearchUnits.rejected, (state, { payload }) => {
      state.unitsLoading = false;
      state.unitsError = payload?.error || true;
    });

    builder.addCase(accountAppsTransmissionStats.pending, (state) => {
      state.statsLoading = true;
      state.statsError = null;
    });
    builder.addCase(accountAppsTransmissionStats.fulfilled, (state, { payload }) => {
      state.statsLoading = false;
      state.stats = payload;
    });
    builder.addCase(accountAppsTransmissionStats.rejected, (state, { payload }) => {
      state.statsLoading = false;
      state.statsError = payload?.error || true;
    });

    builder.addCase(accountAppsSearchTransmissions.pending, (state, { meta }) => {
      if (meta.arg.mode === "prepend") return;
      state.log.loading = true;
      state.log.error = null;
      state.log.requestId = meta.requestId;
    });
    builder.addCase(accountAppsSearchTransmissions.fulfilled, (state, { payload, meta }) => {
      if (!state.log.open || !matchesLogFilter(state.log, meta.arg)) return;
      if (payload.mode === "prepend") {
        if (!payload.rows.length) return;
        const fresh = payload.rows.map((row) => ({ ...row, receivedAt: payload.receivedAt }));
        const { rows, trimmed } = mergeTransmissions(fresh, state.log.rows);
        state.log.rows = rows;
        state.log.hasMore = state.log.hasMore || trimmed;
        return;
      }
      // Only the latest load applies: a slower, older one must not replace it
      if (meta.requestId !== state.log.requestId) return;
      state.log.loading = false;
      state.log.rows = payload.rows;
      state.log.hasMore = payload.hasMore;
    });
    builder.addCase(accountAppsSearchTransmissions.rejected, (state, { payload, meta }) => {
      if (meta.arg.mode === "prepend" || meta.requestId !== state.log.requestId) return;
      state.log.loading = false;
      state.log.error = payload?.error || true;
    });

    builder.addCase(accountAppsExportTransmissions.pending, (state) => {
      state.log.exporting = true;
    });
    builder.addCase(accountAppsExportTransmissions.fulfilled, (state) => {
      state.log.exporting = false;
    });
    builder.addCase(accountAppsExportTransmissions.rejected, (state) => {
      state.log.exporting = false;
    });
  },
});

export const { actions, reducer } = accountAppsRedux;
export const { reset, openAppsWizard, openAppsDelete, openAppsEdit, clearAppsFlash, appsSyncFinished, appsSyncProgress, openAppsConsole, openAppsLog, setAppsLogFilter } = actions;
export default reducer;
